import { gql, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { FolderId } from 'modules/domains/foldering';
import { CurrentSelection } from 'utils/contexts/selection';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import noOp from 'utils/noop';
import { NamedFolder, ResponseWrapper } from 'ui/toast';
import { useEffect } from 'react';

export const RenameFolderMutation = gql`
  mutation RenameCampaignFolder($folderId: Long!, $newName: String!) {
    renameCampaignFolder(folderId: $folderId, newName: $newName) {
      ... on FolderRenamed {
        success {
          folderId
          folderName
        }
      }
      ... on RenameFailure {
        failure {
          ... on CannotRenameRootFolderFailure {
            id
          }
          ... on FolderNameConflictFailure {
            id
            name
          }
          ... on InvalidFolderNameFailure {
            id
            name
          }
        }
      }
    }
  }
`;

type RenameSuccess = {
  folderId: FolderId;
  folderName: string;
};

type RenameFailure = {
  id: FolderId;
  name: string;
};

export type RenameFolderResponseData = {
  renameCampaignFolder: {
    success: RenameSuccess;
    failure: RenameFailure;
  };
};

export type Variables = {
  folderId: FolderId;
  newName: string;
};

type ConfigProps = {
  folder: CurrentSelection;
  handleComplete?: () => void;
  handleError?: () => void;
  handleSuccess: () => void;
};

export function useRenameFolder({
  folder,
  handleComplete = noOp,
  handleError = noOp,
  handleSuccess = noOp,
}: ConfigProps) {
  const dispatch = useDispatch();
  const [renameFolder, { called, loading, error, data }] = useMutation<
    RenameFolderResponseData,
    Variables
  >(RenameFolderMutation);

  useEffect(() => {
    if (!called || loading) {
      return;
    }

    if (error) {
      dispatch(addNegativeToastWithErrorId('Could not rename folder', 'useRenameFolder', error));
    } else if (data?.renameCampaignFolder) {
      const { failure, success } = data.renameCampaignFolder;

      if (failure) {
        const errorMessage = (
          <ResponseWrapper>
            <NamedFolder name={folder.name} /> could not be renamed
            {failure.name ? (
              <span>
                {' '}
                to <NamedFolder name={failure.name || ''} /> folder.
              </span>
            ) : (
              '.'
            )}
          </ResponseWrapper>
        );

        dispatch(addNegativeToastWithErrorId(errorMessage, 'useRenameFolder', error));
        handleError();
      } else if (success) {
        dispatch(
          addPositiveToast(
            <ResponseWrapper>
              <NamedFolder name={folder.name} /> was successfully renamed to{' '}
              <NamedFolder name={success.folderName} /> folder.
            </ResponseWrapper>,
          ),
        );
        handleSuccess();
      }
    }

    handleComplete();
  }, [called, loading, error, data]);

  return [renameFolder, { called, loading, error, data }] as const;
}
